import { useQueryClient } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { removeDownloadHistoryFile, removeDownloadHistoryFiles } from '@api/downloadHistory';

import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { IDownloadHistoryFilterDTO } from '@interfaces/downloadHistory/downloadHistoryDTO';

import { EMenuActionsTypes, ESnackbarTypes } from '@enums/common';

import { allowedRolesDownloadHistory } from '@constants/downloadHistory';
import {
	DOWNLOAD_HISTORY,
	DOWNLOAD_HISTORY_DELETE_FILES,
} from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

interface IDeleteDownloadHistoryParams extends IDownloadHistoryFilterDTO {
	onSuccess: () => void;
}

interface IDeleteDownloadHistoryReturnType {
	deleteFiles: (dto: IGroupOperationWithActionDTO) => void;
	isLoadingDeleteDownloadHistory: boolean;
}

export const useMutateDeleteDownloadHistory = (
	params: IDeleteDownloadHistoryParams,
): IDeleteDownloadHistoryReturnType => {
	const dispatch = useAppDispatch();
	const queryClient = useQueryClient();

	const {
		onSuccess,
		source,
		status,
		searchValue,
	} = params || {};

	const {
		mutate: deleteFiles,
		isLoading: isLoadingDeleteFiles,
	} = useMutation<IGroupOperationWithActionDTO>({
		mutationKey: [DOWNLOAD_HISTORY_DELETE_FILES],
		allowedRoles: allowedRolesDownloadHistory,
		mutationFn: ({ ids, actionType }: IGroupOperationWithActionDTO) => {
			const oneFile = ids.length === 1;
			if (oneFile) {
				removeDownloadHistoryFile({ id: ids[0] });
			} else {
				const filter: IDownloadHistoryFilterDTO = {
					source,
					status,
					searchValue,
				};

				removeDownloadHistoryFiles({
					filter: (actionType === EMenuActionsTypes.allFiltered) && filter
						? filter
						: null,
					ids,
					isAll: actionType === EMenuActionsTypes.all || actionType === EMenuActionsTypes.allFiltered,
				});
			}

			return ids;
		},
		onSuccess: (ids: string[]) => {
			const oneFile = ids.length === 1;

			if (onSuccess) onSuccess();

			dispatch(setSnackbar({
				message: oneFile
					? responseMessages.deleteDownloadHistoryFile
					:responseMessages.deleteDownloadHistoryFiles,
				type: ESnackbarTypes.success,
			}));

			return [queryClient.invalidateQueries([DOWNLOAD_HISTORY])];
		},
	});

	return {
		deleteFiles,
		isLoadingDeleteDownloadHistory: isLoadingDeleteFiles,
	};
};
